import { SegmentationData } from '~/hooks/workflows/backend/tasks/useSegmentationMetrics';

/** Rendered by the table when a column formats to null. */
export const NOT_AVAILABLE = '—';

const MM3_PER_CM3 = 1000;
const MM_PER_CM = 10;

export type SegmetricsColumn = {
    /** Stable identifier, persisted in local storage for column visibility. */
    id: string;
    /** react-intl message id; the label must include the display unit. */
    labelId: string;
    /** Always rendered and excluded from the column picker. */
    alwaysVisible: boolean;
    defaultVisible: boolean;
    /** Null when the value is missing or cannot be computed. */
    format: (item: SegmentationData) => string | null;
};

/**
 * Format a raw metric value for display: guard missing values, apply unit
 * conversion via `divisor`, fix precision, and strip negative-zero. Shared
 * with the per-ROI contour statistics panel so both surfaces format metrics
 * identically.
 */
export function formatMetric(
    value: number | null | undefined,
    decimals: number,
    divisor = 1
): string | null {
    // Guard the RAW value before unit conversion: null / divisor === 0
    // would fabricate a plausible-looking zero for a missing metric
    if (value == null || !Number.isFinite(value)) {
        return null;
    }
    const formatted = (value / divisor).toFixed(decimals);
    // toFixed keeps the sign of tiny negatives (-0.004 -> '-0.0')
    return /^-0(\.0+)?$/.test(formatted) ? formatted.slice(1) : formatted;
}

export const STRUCTURE_COLUMN_ID = 'structure';

function metricColumn(
    id: string,
    defaultVisible: boolean,
    decimals: number,
    select: (item: SegmentationData) => number | null,
    divisor = 1
): SegmetricsColumn {
    return {
        id,
        labelId: `segmetrics.column.${id}`,
        alwaysVisible: false,
        defaultVisible,
        format: (item) => formatMetric(select(item), decimals, divisor),
    };
}

/**
 * All segmetrics result columns in display order. Volumes are converted
 * mm³ -> cm³ and z lengths mm -> cm here at the UI boundary; surface
 * metrics (DSC, HD, APL) keep their clinically conventional mm units
 * (documented exception in docs/ui_specs.md).
 */
export const SEGMETRICS_COLUMNS: SegmetricsColumn[] = [
    {
        id: STRUCTURE_COLUMN_ID,
        labelId: `segmetrics.column.${STRUCTURE_COLUMN_ID}`,
        alwaysVisible: true,
        defaultVisible: true,
        format: (item) => item.gt_roi_name,
    },
    metricColumn(
        'testVolume',
        true,
        2,
        (item) => item.user_vol_mm3,
        MM3_PER_CM3
    ),
    metricColumn(
        'referenceVolume',
        true,
        2,
        (item) => item.gt_vol_mm3,
        MM3_PER_CM3
    ),
    metricColumn(
        'volumeDifference',
        true,
        1,
        (item) =>
            ((item.user_vol_mm3 - item.gt_vol_mm3) / item.gt_vol_mm3) * 100
    ),
    metricColumn('dice', true, 3, (item) => item.dice),
    metricColumn('sdice1', false, 3, (item) => item.sdice1),
    metricColumn('sdice2', true, 3, (item) => item.sdice2),
    metricColumn('sdice3', false, 3, (item) => item.sdice3),
    metricColumn('sdice5', false, 3, (item) => item.sdice5),
    metricColumn('hd95', true, 1, (item) => item.hd95),
    metricColumn('hdMax', false, 1, (item) => item.hd),
    metricColumn('meanHd', false, 2, (item) => item.mean_hd),
    metricColumn('averageApl', false, 1, (item) => item.average_apl_mm),
    metricColumn('totalApl', false, 1, (item) => item.total_apl_mm),
    metricColumn(
        'testZLength',
        false,
        2,
        (item) => item.user_z_length_mm,
        MM_PER_CM
    ),
    metricColumn(
        'referenceZLength',
        false,
        2,
        (item) => item.gt_z_length_mm,
        MM_PER_CM
    ),
];

/** Columns the user can show/hide via the column picker. */
export const TOGGLEABLE_COLUMNS = SEGMETRICS_COLUMNS.filter(
    (column) => !column.alwaysVisible
);

/** Persisted visibility only tracks toggleable columns. */
export const DEFAULT_VISIBLE_COLUMN_IDS = TOGGLEABLE_COLUMNS.filter(
    (column) => column.defaultVisible
).map((column) => column.id);
